'use client';

import { ReactNode, useCallback, useMemo, useState } from 'react';
import useMap from '@/hooks/useMap';
import type { LocationVenueRates } from '@/models/LocationVenueRates';
import Icon from '@/components/shared/Icons';
import type { Locale } from '@/i18n';
import { formatCurrency, formatMessage } from '@/utils/translationHelper';
import { Link } from '@/lib/router-events/patch-router/link';
import dynamic from 'next/dynamic';
import getVenueRatesClickFact, {
  type EntityClickedProps,
  getEntityClicked,
  getMetadata,
  getPageData
} from '@/analytics/helpers/cventAnalytics/getVenueRatesClickFact';
import useAnalytics from '@/hooks/useAnalytics';
import {
  CITY_GUIDE,
  CITY_NAME,
  HIDE_VENUE_RATES_GRAPH,
  SEARCH_RESULTS_PAGE,
  VIEW_VENUE_RATES_GRAPH
} from '@/utils/analyticsClientHelper';
import { getStartAndEndMonthIndex } from '@/utils/venueRatesHelper';
import type { VenueRatesClickSimpleFact } from '@/analytics/models/cventAnalytics/VenueRatesClickSimpleFact';
import { NUMBER_OF_MONTHS } from '@/constants/venueRatesConstants';
import { VIEW, queryStringConstants } from '@/constants/queryConstants';
import { useSearchParams } from 'next/navigation';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import { MAP_SIZE, type MapView } from '@/constants/mapConstants';

const Modal = dynamic(() => import('@/components/shared/modal'), { ssr: false });
const VenueRatesDetails = dynamic(() => import('@/components/venue-rates/VenueRatesDetails'), { ssr: false });
const ViewVenueRates = dynamic(() => import('@/components/venue-rates/VenueRatesModal'), { ssr: false });

type Props = {
  venueRates: LocationVenueRates;
  ratesForNearbyCities: LocationVenueRates;
  strings: {
    exploreArea: string;
    averageRoomCostText: string;
    venueRatesExpandButton: string;
    guestRoomAvgMonthlyRatesText: string;
    viewVenueRatesDetailsText: string;
    venueRatesModalTableLocationHeader: string;
    venueRatesModalTableButton: string;
    venueRatesModalChartButton: string;
    venueRatesModalNoteMsg: string;
    venueRatesModalSubTitle: string;
    venueRatesModalWarningMsg: string;
    venueRatesDropdownStartDateLabel: string;
    venueRatesDropdownEndDateLabel: string;
    venueRatesModalCloseVenueRates: string;
  };
  locale: Locale;
  avgRoomCost: string;
  dgExists: boolean | undefined;
  href: string;
  visitDestinationGuideText: string;
  dgCityName: string | undefined;
  eventStartDate?: Date;
  dgUrlWithoutRelativePath: string;
  searchTerm: string;
  environment: string;
  visitCityText: string;
};

type ExploreAreaContentProps = {
  text: string;
  avgRoomCost: string;
  avgRoomCostText: string;
  locale: Locale;
  venueRatesModal: boolean;
};

type DgLinkProps = {
  venueRatesModal: boolean;
  url: string;
  city: string | undefined;
  locale: Locale;
  msg: string;
  mapView: MapView;
  onClick: () => void;
  isDesktop: boolean;
  visitCityText: string;
};
function ExploreAreaContent({
  text,
  avgRoomCost,
  avgRoomCostText,
  locale,
  venueRatesModal
}: ExploreAreaContentProps): JSX.Element {
  const formattedAvgRoomCost = formatCurrency(locale, parseInt(avgRoomCost, 10), 'USD', 0, 0);
  return (
    <span className={`${venueRatesModal ? 'mx-4 mt-2' : ''}  truncate text-b-md font-medium text-neutral-90`}>
      {text}
      <div className="my-0.5 truncate text-b-xs text-neutral-60">
        <span className="truncate">
          {formatMessage(avgRoomCostText, locale, {
            rate: formattedAvgRoomCost
          })}
        </span>
      </div>
    </span>
  );
}

function VisitCityContent(
  visitCityText: string,
  locale: Locale,
  city: string | undefined,
  url: string,
  onClick: () => void
): string {
  return formatMessage(visitCityText, locale, {
    cityName: city,
    a: (chunks: ReactNode[]) => (
      <Link className="text-brand-60" onClick={onClick} href={url} target="_blank" rel="noreferrer">
        {chunks}
      </Link>
    ),
    br: (chunks: ReactNode[]) => (
      <>
        <br />
        {chunks}
      </>
    )
  });
}

function DgLink({
  venueRatesModal,
  url,
  city,
  locale,
  msg,
  mapView,
  onClick,
  isDesktop,
  visitCityText
}: DgLinkProps): JSX.Element {
  const flyoutText = VisitCityContent(visitCityText, locale, city, url, onClick);
  return (
    <>
      {mapView === MAP_SIZE.MEDIUM && isDesktop ? (
        <div className="ml-4 mr-3 w-full truncate text-right">
          <span className="text-normal text-b-md text-neutral-90">{flyoutText}</span>
        </div>
      ) : (
        <Link
          className={`${venueRatesModal ? 'ml-4' : 'mb-1 ml-1 pl-8'}  ${
            mapView === MAP_SIZE.MEDIUM ? 'pr-4 pt-2' : 'lg:pr-4 lg:pt-2 xl:pr-0 xl:pt-0'
          } w-full truncate text-end text-b-md text-brand-60`}
          href={url}
          target="_blank"
          rel="noreferrer"
          onClick={onClick}
        >
          {formatMessage(msg, locale, { cityName: city })}
        </Link>
      )}
    </>
  );
}

export default function ExploreAreaCard({
  venueRates,
  ratesForNearbyCities,
  strings,
  locale,
  avgRoomCost,
  dgExists,
  href,
  visitDestinationGuideText,
  dgCityName,
  eventStartDate,
  dgUrlWithoutRelativePath,
  searchTerm,
  environment,
  visitCityText
}: Props): JSX.Element {
  const analytics = useAnalytics();
  const {
    mapState,
    setMapState,
    mapView,
    venueRatesModal,
    toggleModal,
    venueRatesExpanded,
    setVenueRatesExpanded,
    setVenueFlyoutId
  } = useMap();
  const params = useSearchParams();
  const view = useMemo(() => params?.get(queryStringConstants.VIEW), [params]);
  const styles = useMemo((): { container: string } => {
    const expanded = `hidden ${
      view === VIEW.FULL_MAP ? 'xl:hidden' : 'lg:block'
    } ms-8 lg:w-7/12 lg:ml-56 xl:w-[90%] xl:ml-8 rounded-xl p-1 bg-background relative ${
      venueRatesExpanded ? '-mt-[345px] mb-5 pt-2' : 'lg:-mt-20 xl:-mt-24 py-2'
    }`;
    const collapsed = `hidden ${
      view === VIEW.FULL_MAP ? 'xl:hidden' : 'lg:block'
    } xl:w-full lg:w-7/12 lg:ml-56 xl:ml-0 rounded-xl bg-background border-solid p-1 relative ${
      venueRatesExpanded ? 'xl:mt-6 lg:-mt-[330px] lg:mb-5 xl:mb-0' : 'lg:-mt-20 xl:mt-6 py-2'
    }`;
    return { container: mapView === MAP_SIZE.MEDIUM ? expanded : collapsed };
  }, [mapView, venueRatesExpanded, view]);

  const triggerVenueRatesClickFact = useCallback(
    (
      startIndex: number,
      endIndex: number,
      isOutsideRange: boolean,
      entityClicked: EntityClickedProps,
      page: string,
      viewMode?: number
    ) => {
      const locationVenueRates = venueRates.at(0);
      if (locationVenueRates) {
        const metaData = getMetadata(
          viewMode || 0,
          startIndex!,
          endIndex!,
          isOutsideRange,
          locationVenueRates.location.stateCode,
          locationVenueRates.location.countryCode,
          locationVenueRates.venueRates.futureRates,
          eventStartDate
        );
        const pageData = getPageData(page, searchTerm, locationVenueRates.locationType);
        const entityListed = venueRates.map(venueRate => venueRate.location.name);
        const venueRatesClickFact = getVenueRatesClickFact(
          entityListed,
          entityClicked,
          pageData,
          mapView,
          metaData,
          environment
        );
        analytics.reportFact<VenueRatesClickSimpleFact>(venueRatesClickFact);
      }
    },
    [analytics, environment, eventStartDate, mapView, searchTerm, venueRates]
  );
  const { startIndex, endIndex } = useMemo(
    () => getStartAndEndMonthIndex(venueRates, NUMBER_OF_MONTHS, eventStartDate),
    [eventStartDate, venueRates]
  );
  const openVenueRatesDetails = useCallback(() => {
    triggerVenueRatesClickFact(
      startIndex!,
      endIndex!,
      false,
      getEntityClicked(venueRatesExpanded ? HIDE_VENUE_RATES_GRAPH : VIEW_VENUE_RATES_GRAPH),
      SEARCH_RESULTS_PAGE
    );
    setVenueRatesExpanded(!venueRatesExpanded);
    setVenueFlyoutId('');
    setMapState({ ...mapState, showFlyout: false });
  }, [
    triggerVenueRatesClickFact,
    startIndex,
    endIndex,
    venueRatesExpanded,
    setVenueRatesExpanded,
    setMapState,
    mapState,
    setVenueFlyoutId
  ]);

  const onDgLinkClick = useCallback(() => {
    triggerVenueRatesClickFact(
      startIndex!,
      endIndex!,
      false,
      getEntityClicked(CITY_NAME, CITY_GUIDE, venueRates.at(0)?.destinationGuide.id, dgCityName, 0),
      SEARCH_RESULTS_PAGE
    );
  }, [dgCityName, endIndex, startIndex, triggerVenueRatesClickFact, venueRates]);

  const closeVenueRatesModal = useCallback(() => {
    toggleModal(!venueRatesModal);
  }, [venueRatesModal, toggleModal]);

  const [venueRatesView, setVenueRatesView] = useState(false);
  const { isDesktop } = useBreakpoints();

  return venueRatesModal ? (
    <Modal format="bottom" data-cvent-id="venue-rates-modal" isOpen={venueRatesModal} portal className="lg:invisible">
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div
        onClick={closeVenueRatesModal}
        role="button"
        tabIndex={0}
        aria-label="Close venue rates"
        className="absolute right-3 mt-2.5 flex h-6 w-6"
      >
        <Icon id="x" fill="black" className="left-1/2 top-1/2" />
      </div>
      <div className="text-neutral-60">
        <div className="flex justify-start">
          <ExploreAreaContent
            text={strings.exploreArea}
            avgRoomCost={avgRoomCost}
            avgRoomCostText={strings.averageRoomCostText}
            locale={locale}
            venueRatesModal={venueRatesModal}
          />
        </div>
        {dgExists && (
          <DgLink
            onClick={onDgLinkClick}
            venueRatesModal={venueRatesModal}
            url={href}
            city={dgCityName}
            locale={locale}
            msg={visitDestinationGuideText}
            mapView={mapView}
            isDesktop={isDesktop}
            visitCityText={visitCityText}
          />
        )}
        <VenueRatesDetails
          venueRates={venueRates}
          locale={locale}
          mapView={MAP_SIZE.SMALL}
          guestRoomAvgMonthlyRatesText={strings.guestRoomAvgMonthlyRatesText}
          viewVenueRatesDetailsText={strings.viewVenueRatesDetailsText}
          eventStartDate={eventStartDate}
          venueRatesView={venueRatesView}
          setIsVenueRates={setVenueRatesView}
          triggerVenueRatesClickFact={triggerVenueRatesClickFact}
        />
        {venueRatesView && (
          <ViewVenueRates
            venueRates={ratesForNearbyCities}
            isOpenVenueRates={setVenueRatesView}
            eventStartDate={eventStartDate}
            dgUrlWithoutRelativePath={dgUrlWithoutRelativePath}
            localisedString={strings}
            locale={locale}
            triggerVenueRatesClickFact={triggerVenueRatesClickFact}
          />
        )}
      </div>
    </Modal>
  ) : (
    <div className={styles.container}>
      <div
        className={
          mapView === MAP_SIZE.MEDIUM
            ? 'grid w-full grid-cols-1 text-neutral-60'
            : 'w-full text-neutral-60 lg:grid lg:grid-cols-1 xl:block xl:grid-cols-none'
        }
      >
        <div className={mapView === MAP_SIZE.MEDIUM ? 'flex' : ''}>
          {/* todo: fix this */}
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <button
            onClick={openVenueRatesDetails}
            type="button"
            className={
              mapView === MAP_SIZE.MEDIUM ? 'flex max-w-[50%] justify-start text-left' : 'flex justify-start text-left'
            }
          >
            <Icon
              id={
                mapView === MAP_SIZE.MEDIUM
                  ? `${venueRatesExpanded ? 'chevron-down' : 'chevron-up'}`
                  : `${venueRatesExpanded ? 'chevron-up' : 'chevron-down'}`
              }
              testID={`explore-the-area-${venueRatesExpanded ? 'chevron-up' : 'chevron-down'}`}
              className="m-2 h-5 w-5"
              title={strings.venueRatesExpandButton}
            />
            <ExploreAreaContent
              text={strings.exploreArea}
              avgRoomCost={avgRoomCost}
              avgRoomCostText={strings.averageRoomCostText}
              locale={locale}
              venueRatesModal={venueRatesModal}
            />
          </button>
          {dgExists && (
            <DgLink
              venueRatesModal={venueRatesModal}
              url={href}
              city={dgCityName}
              locale={locale}
              msg={visitDestinationGuideText}
              mapView={mapView}
              onClick={onDgLinkClick}
              isDesktop={isDesktop}
              visitCityText={visitCityText}
            />
          )}
        </div>
        {venueRatesExpanded && (
          <VenueRatesDetails
            venueRates={venueRates}
            locale={locale}
            mapView={mapView}
            guestRoomAvgMonthlyRatesText={strings.guestRoomAvgMonthlyRatesText}
            viewVenueRatesDetailsText={strings.viewVenueRatesDetailsText}
            eventStartDate={eventStartDate}
            venueRatesView={venueRatesView}
            setIsVenueRates={setVenueRatesView}
            triggerVenueRatesClickFact={triggerVenueRatesClickFact}
          />
        )}
        {venueRatesView && (
          <ViewVenueRates
            venueRates={ratesForNearbyCities}
            isOpenVenueRates={setVenueRatesView}
            eventStartDate={eventStartDate}
            dgUrlWithoutRelativePath={dgUrlWithoutRelativePath}
            localisedString={strings}
            locale={locale}
            triggerVenueRatesClickFact={triggerVenueRatesClickFact}
          />
        )}
      </div>
    </div>
  );
}
